"use client";

import { useEffect, useRef, useState, type MutableRefObject } from "react";
import { motion, useReducedMotion } from "motion/react";
import { useTheme } from "next-themes";

type Gpu = {
  name: string;
  rps: number;
};

type ThemeColor = {
  css: string;
  rgb: [number, number, number];
};

type ThemePalette = {
  accent: ThemeColor;
  background: ThemeColor;
  foreground: ThemeColor;
  isDark: boolean;
  mutedForeground: ThemeColor;
};

type ParticleLayout = {
  count: number;
  data: Float32Array;
};

const GPUS: readonly Gpu[] = [
  { name: "B200", rps: 93.72 },
  { name: "H100", rps: 66.38 },
  { name: "L40S", rps: 26.42 },
  { name: "A100", rps: 26.16 },
  { name: "A10", rps: 8.88 },
  { name: "L4", rps: 7.78 },
  { name: "Jetson", rps: 4.63 },
] as const;

const MAX_RPS = Math.max(...GPUS.map((gpu) => gpu.rps));
const EASE: [number, number, number, number] = [0.16, 1, 0.3, 1];
const STAGGER = 0.2;
const STREAM_END_GAP_PX = 16;
const PARTICLE_STRIDE = 6;
const PARTICLE_STRIDE_BYTES = PARTICLE_STRIDE * Float32Array.BYTES_PER_ELEMENT;

const VERTEX_SHADER = `
attribute float a_lane;
attribute float a_phase;
attribute float a_speed;
attribute float a_y_off;
attribute float a_size;
attribute float a_alpha;

uniform vec2 u_resolution;
uniform float u_time;
uniform float u_lane_height;
uniform float u_stream_width;
uniform float u_dpr;

varying float v_alpha;
varying float v_size_mix;

void main() {
  float x_norm = fract(a_phase + u_time * a_speed);
  float x = x_norm * u_stream_width;
  float y = (a_lane + 0.5) * u_lane_height + a_y_off * u_lane_height;

  float clip_x = (x / u_resolution.x) * 2.0 - 1.0;
  float clip_y = 1.0 - (y / u_resolution.y) * 2.0;

  gl_Position = vec4(clip_x, clip_y, 0.0, 1.0);
  gl_PointSize = a_size * u_dpr;

  v_alpha = a_alpha * smoothstep(0.0, 40.0, x);
  v_size_mix = clamp((a_size - 14.0) / 10.0, 0.0, 1.0);
}`;

const FRAGMENT_SHADER = `
precision mediump float;

uniform vec3 u_accent;
uniform vec3 u_highlight;

varying float v_alpha;
varying float v_size_mix;

void main() {
  vec2 uv = gl_PointCoord * 2.0 - 1.0;

  float trail = exp(
    -(
      (uv.x + 0.55) * (uv.x + 0.55) * 3.2 +
      uv.y * uv.y * 16.0
    )
  );
  float glow = exp(
    -(
      uv.x * uv.x * 7.5 +
      uv.y * uv.y * 11.5
    )
  );
  float core = exp(
    -(
      uv.x * uv.x * 28.0 +
      uv.y * uv.y * 28.0
    )
  );
  float hot = exp(
    -(
      uv.x * uv.x * 92.0 +
      uv.y * uv.y * 92.0
    )
  );

  float alphaShape = trail * 0.26 + glow * 0.42 + core * 0.82;
  float alpha = v_alpha * alphaShape;

  if (alpha < 0.01) {
    discard;
  }

  float highlightMix = clamp(hot * (0.18 + v_size_mix * 0.08), 0.0, 0.26);
  vec3 color = mix(u_accent, u_highlight, highlightMix);

  gl_FragColor = vec4(color * alpha, alpha);
}`;

const FALLBACK_PALETTES: Record<"dark" | "light", ThemePalette> = {
  dark: {
    accent: { css: "rgb(85, 180, 214)", rgb: [85, 180, 214] },
    background: { css: "rgb(36, 35, 41)", rgb: [36, 35, 41] },
    foreground: { css: "rgb(250, 250, 250)", rgb: [250, 250, 250] },
    isDark: true,
    mutedForeground: { css: "rgb(165, 163, 175)", rgb: [165, 163, 175] },
  },
  light: {
    accent: { css: "rgb(74, 174, 211)", rgb: [74, 174, 211] },
    background: { css: "rgb(250, 249, 246)", rgb: [250, 249, 246] },
    foreground: { css: "rgb(26, 24, 24)", rgb: [26, 24, 24] },
    isDark: false,
    mutedForeground: { css: "rgb(118, 113, 113)", rgb: [118, 113, 113] },
  },
};

function mixRgb(
  from: [number, number, number],
  to: [number, number, number],
  amount: number
): [number, number, number] {
  return [
    Math.round(from[0] + (to[0] - from[0]) * amount),
    Math.round(from[1] + (to[1] - from[1]) * amount),
    Math.round(from[2] + (to[2] - from[2]) * amount),
  ];
}

function compileShader(
  gl: WebGLRenderingContext,
  type: number,
  source: string
): WebGLShader | null {
  const shader = gl.createShader(type);
  if (!shader) return null;

  gl.shaderSource(shader, source);
  gl.compileShader(shader);

  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    console.error(gl.getShaderInfoLog(shader));
    gl.deleteShader(shader);
    return null;
  }

  return shader;
}

function createProgram(
  gl: WebGLRenderingContext,
  vertexSource: string,
  fragmentSource: string
): WebGLProgram | null {
  const vertexShader = compileShader(gl, gl.VERTEX_SHADER, vertexSource);
  const fragmentShader = compileShader(gl, gl.FRAGMENT_SHADER, fragmentSource);

  if (!vertexShader || !fragmentShader) return null;

  const program = gl.createProgram();
  if (!program) {
    gl.deleteShader(vertexShader);
    gl.deleteShader(fragmentShader);
    return null;
  }

  gl.attachShader(program, vertexShader);
  gl.attachShader(program, fragmentShader);
  gl.linkProgram(program);

  gl.deleteShader(vertexShader);
  gl.deleteShader(fragmentShader);

  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    console.error(gl.getProgramInfoLog(program));
    gl.deleteProgram(program);
    return null;
  }

  return program;
}

function resolveColor(
  probe: HTMLDivElement,
  variable: string,
  fallback: ThemeColor
): ThemeColor {
  probe.style.color = `var(${variable}, ${fallback.css})`;
  const resolved = window.getComputedStyle(probe).color;
  const parts = resolved.match(/[\d.]+/g);

  if (!parts || parts.length < 3) {
    return fallback;
  }

  return {
    css: resolved,
    rgb: [
      Math.round(Number(parts[0])),
      Math.round(Number(parts[1])),
      Math.round(Number(parts[2])),
    ],
  };
}

function readPalette(theme: "dark" | "light"): ThemePalette {
  const fallback = FALLBACK_PALETTES[theme];
  const probe = document.createElement("div");
  probe.style.opacity = "0";
  probe.style.pointerEvents = "none";
  probe.style.position = "absolute";
  document.body.appendChild(probe);

  const palette: ThemePalette = {
    accent: resolveColor(probe, "--chart-2", fallback.accent),
    background: resolveColor(probe, "--background", fallback.background),
    foreground: resolveColor(probe, "--foreground", fallback.foreground),
    isDark: theme === "dark",
    mutedForeground: resolveColor(
      probe,
      "--muted-foreground",
      fallback.mutedForeground
    ),
  };

  probe.remove();
  return palette;
}

function createParticleLayout(): ParticleLayout {
  const laneCounts = GPUS.map((gpu) =>
    Math.round(12 + (gpu.rps / MAX_RPS) * 84)
  );
  const count = laneCounts.reduce((sum, laneCount) => sum + laneCount, 0);
  const data = new Float32Array(count * PARTICLE_STRIDE);

  let cursor = 0;

  for (let laneIndex = 0; laneIndex < GPUS.length; laneIndex += 1) {
    const ratio = GPUS[laneIndex].rps / MAX_RPS;
    const laneCount = laneCounts[laneIndex];
    const speedBase = 0.18 + ratio * 0.18;

    for (let index = 0; index < laneCount; index += 1) {
      data[cursor] = laneIndex;
      data[cursor + 1] = Math.random();
      data[cursor + 2] = speedBase * (0.82 + Math.random() * 0.36);
      data[cursor + 3] = (Math.random() - 0.5) * 0.42;
      data[cursor + 4] = 14 + Math.random() * 10;
      data[cursor + 5] = 0.55 + Math.random() * 0.45;
      cursor += PARTICLE_STRIDE;
    }
  }

  return { count, data };
}

function measureStreamWidth(
  canvasWrap: HTMLDivElement,
  metricRefs: MutableRefObject<(HTMLDivElement | null)[]>
) {
  const width = canvasWrap.offsetWidth;
  const canvasLeft = canvasWrap.getBoundingClientRect().left;
  const metricLefts = metricRefs.current
    .map((metric) => metric?.getBoundingClientRect().left)
    .filter((left): left is number => left !== undefined);
  const streamEnd =
    metricLefts.length > 0
      ? Math.min(...metricLefts) - canvasLeft - STREAM_END_GAP_PX
      : width;

  return Math.max(0, Math.min(width, streamEnd));
}

function AnimatedCounter({
  value,
  delay,
}: {
  value: number;
  delay: number;
}) {
  const ref = useRef<HTMLSpanElement | null>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    let frameId = 0;
    const duration = 1.8;
    const timeoutId = window.setTimeout(() => {
      const start = performance.now();

      const tick = (now: number) => {
        const progress = Math.min(1, (now - start) / (duration * 1000));
        const eased = 1 - (1 - progress) ** 4;
        element.textContent = (value * eased).toFixed(1);

        if (progress < 1) {
          frameId = requestAnimationFrame(tick);
        }
      };

      frameId = requestAnimationFrame(tick);
    }, delay * 1000);

    return () => {
      clearTimeout(timeoutId);
      if (frameId) cancelAnimationFrame(frameId);
    };
  }, [delay, value]);

  return (
    <span
      ref={ref}
      className="font-mono text-lg font-bold tabular-nums text-foreground"
    >
      0.0
    </span>
  );
}

function PerfAnimation() {
  const { resolvedTheme } = useTheme();
  const shouldReduceMotion = useReducedMotion();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const canvasWrapRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const metricRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [palette, setPalette] = useState<ThemePalette>(
    FALLBACK_PALETTES.light
  );

  useEffect(() => {
    const theme =
      resolvedTheme === "light"
        ? "light"
        : resolvedTheme === "dark"
          ? "dark"
          : document.documentElement.classList.contains("dark")
            ? "dark"
            : "light";

    setPalette(readPalette(theme));
  }, [resolvedTheme]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const canvasWrap = canvasWrapRef.current;
    const container = containerRef.current;

    if (!canvas || !canvasWrap || !container) {
      return;
    }

    const glOptions = {
      alpha: true,
      antialias: false,
      depth: false,
      desynchronized: true,
      powerPreference: "high-performance" as const,
      premultipliedAlpha: true,
      preserveDrawingBuffer: false,
      stencil: false,
    };

    const gl = (
      canvas.getContext("webgl2", glOptions) ||
      canvas.getContext("webgl", glOptions)
    ) as WebGLRenderingContext | null;

    if (!gl) {
      return;
    }

    const program = createProgram(gl, VERTEX_SHADER, FRAGMENT_SHADER);
    if (!program) {
      return;
    }

    const buffer = gl.createBuffer();
    if (!buffer) {
      gl.deleteProgram(program);
      return;
    }

    const layout = createParticleLayout();

    const laneLocation = gl.getAttribLocation(program, "a_lane");
    const phaseLocation = gl.getAttribLocation(program, "a_phase");
    const speedLocation = gl.getAttribLocation(program, "a_speed");
    const yOffLocation = gl.getAttribLocation(program, "a_y_off");
    const sizeLocation = gl.getAttribLocation(program, "a_size");
    const alphaLocation = gl.getAttribLocation(program, "a_alpha");
    const resolutionLocation = gl.getUniformLocation(program, "u_resolution");
    const timeLocation = gl.getUniformLocation(program, "u_time");
    const laneHeightLocation = gl.getUniformLocation(program, "u_lane_height");
    const streamWidthLocation = gl.getUniformLocation(program, "u_stream_width");
    const dprLocation = gl.getUniformLocation(program, "u_dpr");
    const accentLocation = gl.getUniformLocation(program, "u_accent");
    const highlightLocation = gl.getUniformLocation(program, "u_highlight");

    if (
      laneLocation < 0 ||
      phaseLocation < 0 ||
      speedLocation < 0 ||
      yOffLocation < 0 ||
      sizeLocation < 0 ||
      alphaLocation < 0 ||
      !resolutionLocation ||
      !timeLocation ||
      !laneHeightLocation ||
      !streamWidthLocation ||
      !dprLocation ||
      !accentLocation ||
      !highlightLocation
    ) {
      gl.deleteBuffer(buffer);
      gl.deleteProgram(program);
      return;
    }

    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, layout.data, gl.STATIC_DRAW);

    gl.useProgram(program);
    gl.enable(gl.BLEND);
    gl.blendFuncSeparate(gl.ONE, gl.ONE_MINUS_SRC_ALPHA, gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    gl.disable(gl.DEPTH_TEST);

    gl.enableVertexAttribArray(laneLocation);
    gl.vertexAttribPointer(
      laneLocation,
      1,
      gl.FLOAT,
      false,
      PARTICLE_STRIDE_BYTES,
      0
    );

    gl.enableVertexAttribArray(phaseLocation);
    gl.vertexAttribPointer(
      phaseLocation,
      1,
      gl.FLOAT,
      false,
      PARTICLE_STRIDE_BYTES,
      Float32Array.BYTES_PER_ELEMENT
    );

    gl.enableVertexAttribArray(speedLocation);
    gl.vertexAttribPointer(
      speedLocation,
      1,
      gl.FLOAT,
      false,
      PARTICLE_STRIDE_BYTES,
      Float32Array.BYTES_PER_ELEMENT * 2
    );

    gl.enableVertexAttribArray(yOffLocation);
    gl.vertexAttribPointer(
      yOffLocation,
      1,
      gl.FLOAT,
      false,
      PARTICLE_STRIDE_BYTES,
      Float32Array.BYTES_PER_ELEMENT * 3
    );

    gl.enableVertexAttribArray(sizeLocation);
    gl.vertexAttribPointer(
      sizeLocation,
      1,
      gl.FLOAT,
      false,
      PARTICLE_STRIDE_BYTES,
      Float32Array.BYTES_PER_ELEMENT * 4
    );

    gl.enableVertexAttribArray(alphaLocation);
    gl.vertexAttribPointer(
      alphaLocation,
      1,
      gl.FLOAT,
      false,
      PARTICLE_STRIDE_BYTES,
      Float32Array.BYTES_PER_ELEMENT * 5
    );

    let width = 0;
    let height = 0;
    let streamWidth = 0;
    let dpr = 1;
    let frameId = 0;
    let visible = true;
    const startTime = performance.now();

    const resize = () => {
      width = canvasWrap.offsetWidth;
      height = canvasWrap.offsetHeight;

      if (!width || !height) {
        return;
      }

      dpr = Math.min(window.devicePixelRatio || 1, 1.5);
      streamWidth = measureStreamWidth(canvasWrap, metricRefs);

      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;

      gl.viewport(0, 0, canvas.width, canvas.height);
      gl.useProgram(program);
      gl.uniform2f(resolutionLocation, width, height);
      gl.uniform1f(laneHeightLocation, height / GPUS.length);
      gl.uniform1f(streamWidthLocation, streamWidth);
      gl.uniform1f(dprLocation, dpr);
    };

    const draw = (now: number) => {
      if (!width || !height || !streamWidth) {
        return;
      }

      const [accentR, accentG, accentB] = palette.accent.rgb;
      const [highlightR, highlightG, highlightB] = palette.isDark
        ? mixRgb(palette.accent.rgb, palette.foreground.rgb, 0.2)
        : mixRgb(palette.accent.rgb, palette.background.rgb, 0.18);

      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.useProgram(program);
      gl.uniform1f(
        timeLocation,
        shouldReduceMotion ? 0 : (now - startTime) * 0.001
      );
      gl.uniform3f(
        accentLocation,
        accentR / 255,
        accentG / 255,
        accentB / 255
      );
      gl.uniform3f(
        highlightLocation,
        highlightR / 255,
        highlightG / 255,
        highlightB / 255
      );

      gl.enable(gl.SCISSOR_TEST);
      gl.scissor(0, 0, Math.max(1, Math.round(streamWidth * dpr)), canvas.height);
      gl.drawArrays(gl.POINTS, 0, layout.count);
      gl.disable(gl.SCISSOR_TEST);
    };

    const tick = (now: number) => {
      if (!visible || shouldReduceMotion) {
        frameId = 0;
        return;
      }

      draw(now);
      frameId = requestAnimationFrame(tick);
    };

    resize();
    draw(performance.now());

    const resizeObserver = new ResizeObserver(() => {
      resize();
      draw(performance.now());
    });

    resizeObserver.observe(container);
    resizeObserver.observe(canvasWrap);
    metricRefs.current.forEach((metric) => {
      if (metric) {
        resizeObserver.observe(metric);
      }
    });

    const intersectionObserver = new IntersectionObserver(
      ([entry]) => {
        visible = entry.isIntersecting;

        if (visible) {
          draw(performance.now());

          if (!shouldReduceMotion && !frameId) {
            frameId = requestAnimationFrame(tick);
          }
        } else if (frameId) {
          cancelAnimationFrame(frameId);
          frameId = 0;
        }
      },
      { threshold: 0 }
    );

    intersectionObserver.observe(container);

    if (!shouldReduceMotion) {
      frameId = requestAnimationFrame(tick);
    }

    return () => {
      cancelAnimationFrame(frameId);
      resizeObserver.disconnect();
      intersectionObserver.disconnect();
      gl.deleteBuffer(buffer);
      gl.deleteProgram(program);
    };
  }, [palette, shouldReduceMotion]);

  return (
    <div style={{ position: "relative", width: "100%" }}>
      <div
        ref={containerRef}
        style={{
          position: "relative",
          width: "100%",
          height: "clamp(20rem, 50vw, 28rem)",
        }}
      >
        <div
          ref={canvasWrapRef}
          style={{
            position: "absolute",
            top: 0,
            right: 0,
            bottom: 0,
            left: "clamp(5rem, 20vw, 9rem)",
            zIndex: 1,
            overflow: "hidden",
          }}
        >
          <canvas ref={canvasRef} />
        </div>

        <div
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 2,
            display: "flex",
            flexDirection: "column",
            pointerEvents: "none",
          }}
        >
          {GPUS.map((gpu, index) => (
            <motion.div
              key={gpu.name}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{
                delay: 0.3 + index * STAGGER,
                duration: 0.7,
                ease: EASE,
              }}
              style={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                paddingLeft: "clamp(1rem, 4vw, 2.5rem)",
                paddingRight: "clamp(1rem, 4vw, 2.5rem)",
              }}
            >
              <span className="text-sm font-semibold text-foreground sm:text-base">
                {gpu.name}
              </span>
              <div
                ref={(element) => {
                  metricRefs.current[index] = element;
                }}
                className="text-right"
              >
                <AnimatedCounter
                  value={gpu.rps}
                  delay={0.5 + index * STAGGER}
                />
                <div className="font-mono text-[10px] text-muted-foreground/50">
                  req/s
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default PerfAnimation;
export { PerfAnimation };
